import { thumbUrl } from "../lib/api";
import { AtSign, Loader2 } from "lucide-react";

/**
 * Popover that lists slugged references matching the current `@query`
 * being typed in a chat composer. Positioned by the parent (absolute
 * above the textarea) — this component is presentation-only.
 *
 * Keyboard driving lives in the parent so the textarea keeps the focus
 * ring (accessibility + native behaviours like Shift+Enter for newline).
 */
export default function MentionAutocomplete({
    active,
    query,
    loading,
    items,
    activeIdx,
    onSelect,
}) {
    if (!active) return null;
    return (
        <div
            data-testid="chat-mention-popover"
            className="absolute left-16 bottom-full mb-2 w-80 max-w-[calc(100%-8rem)] rounded-xl border hairline border-ink-600 bg-ink-900/95 backdrop-blur-md shadow-2xl overflow-hidden z-30 animate-fade-up"
        >
            <div className="px-3 py-2 border-b hairline border-ink-700 flex items-center gap-2">
                <AtSign className="w-3 h-3 text-periwinkle-400" />
                <span className="font-mono text-[10px] tracking-widest text-fog-400 uppercase">
                    Referencias con mención
                </span>
                {loading && (
                    <Loader2 className="w-3 h-3 text-fog-500 animate-spin ml-auto" />
                )}
                {!loading && query && (
                    <span className="ml-auto font-mono text-[10px] text-fog-500">
                        @{query}
                    </span>
                )}
            </div>
            {items.length === 0 && !loading && (
                <div className="px-3 py-4 text-center">
                    <p className="text-fog-500 text-xs">
                        Sin resultados{query ? ` para @${query}` : ""}.
                    </p>
                    <p className="text-fog-600 text-[10px] mt-1">
                        Asigna un nombre en /references para mencionar tus imágenes.
                    </p>
                </div>
            )}
            <ul
                data-testid="chat-mention-list"
                className="max-h-64 overflow-y-auto py-1"
            >
                {items.map((it, idx) => {
                    const active = idx === activeIdx;
                    return (
                        <li key={it.id}>
                            <button
                                type="button"
                                data-testid={`chat-mention-option-${it.slug}`}
                                // onMouseDown fires BEFORE the textarea blurs,
                                // preventing the caret from jumping when the
                                // user clicks with the pointer.
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    onSelect(it);
                                }}
                                className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${
                                    active
                                        ? "bg-periwinkle-500/15"
                                        : "hover:bg-ink-800"
                                }`}
                            >
                                <div className="w-9 h-9 shrink-0 rounded-md overflow-hidden border hairline border-ink-600 bg-ink-950">
                                    {it.storage_path && (
                                        <img
                                            src={thumbUrl(it.storage_path, 80)}
                                            alt={it.slug}
                                            className="w-full h-full object-cover"
                                            loading="lazy"
                                        />
                                    )}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p
                                        className={`font-mono text-[11px] tracking-wider truncate ${
                                            active
                                                ? "text-periwinkle-300"
                                                : "text-fog-100"
                                        }`}
                                    >
                                        @{it.slug}
                                    </p>
                                    <p className="text-fog-500 text-[10px] truncate">
                                        {it.title || "sin título"}
                                    </p>
                                </div>
                            </button>
                        </li>
                    );
                })}
            </ul>
            <div className="px-3 py-1.5 border-t hairline border-ink-700 flex items-center gap-2 font-mono text-[9px] tracking-widest text-fog-500 uppercase">
                <kbd className="rounded border hairline border-ink-700 px-1 text-[9px] text-fog-300">
                    ↑↓
                </kbd>
                navegar
                <kbd className="rounded border hairline border-ink-700 px-1 text-[9px] text-fog-300 ml-1">
                    Enter
                </kbd>
                elegir
                <kbd className="rounded border hairline border-ink-700 px-1 text-[9px] text-fog-300 ml-1">
                    Esc
                </kbd>
                cerrar
            </div>
        </div>
    );
}
